// Mirrors the JSON the Go API sends. Kept hand-written rather than generated:
// the surface is small, and a hand-written file is one less build step to keep
// working.

export type Role = 'admin' | 'member'
export type ProjectRole = 'owner' | 'member'
export type Priority = 'low' | 'medium' | 'high' | 'urgent'

export interface User {
  id: string
  email: string
  name: string
  avatarUrl: string
  role: Role
  timezone: string
  digestEmail: boolean
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface UserRef {
  id: string
  name: string
  email: string
  avatarUrl: string
}

export interface Project {
  id: string
  key: string
  name: string
  description: string
  color: string
  slackChannelId: string
  slackEvents: string[]
  archivedAt: string | null
  createdAt: string
  updatedAt: string
  /** Set when this is a sub-project of another. Nesting is one level deep. */
  parentId: string | null
  parentKey?: string
  parentName?: string
  /** How many sub-projects this one has. */
  childCount: number
  /** Who new tasks go to unless the person creating one says otherwise. */
  defaultAssigneeId: string | null
  defaultAssignee: UserRef | null
  myRole: ProjectRole | ''
  openTasks: number
}

export interface Member {
  user: UserRef
  role: ProjectRole
  createdAt: string
  /** Access comes from the parent project, so it is changed there. */
  inherited: boolean
}

export interface Status {
  id: string
  projectId: string
  name: string
  color: string
  position: number
  isDone: boolean
}

export interface Tag {
  id: string
  name: string
  color: string
  createdAt: string
  usageCount?: number
}

export interface Task {
  id: string
  projectId: string
  projectKey: string
  projectName: string
  number: number
  key: string
  title: string
  description: string
  statusId: string
  statusName: string
  statusDone: boolean
  position: number
  priority: Priority
  assignee: UserRef | null
  reporter: UserRef | null
  dueDate: string | null
  archivedAt: string | null
  createdAt: string
  updatedAt: string
  tags: Tag[]
  commentCount: number
  attachmentCount: number
  /** Checklist progress, so a card can show 2/5 without loading the items. */
  subtaskCount: number
  subtaskDone: number
}

/**
 * One line of a task's checklist.
 *
 * Its own row with its own id, rather than `- [ ]` inside the description,
 * because marking exactly one item done has to be a single addressable
 * operation — that is what lets an agent tick items off as it works.
 */
export interface Subtask {
  id: string
  taskId: string
  title: string
  done: boolean
  position: number
  createdAt: string
}

export interface Comment {
  id: string
  taskId: string
  author: UserRef | null
  body: string
  editedAt: string | null
  createdAt: string
}

export interface Attachment {
  id: string
  taskId: string
  uploader: UserRef | null
  filename: string
  contentType: string
  sizeBytes: number
  createdAt: string
}

export type ActivityKind =
  | 'created'
  | 'status_changed'
  | 'assigned'
  | 'unassigned'
  | 'title_changed'
  | 'description_changed'
  | 'priority_changed'
  | 'due_changed'
  | 'tags_changed'
  | 'commented'
  | 'attached'
  | 'archived'
  | 'restored'

export interface Activity {
  id: number
  taskId: string
  actor: UserRef | null
  kind: ActivityKind
  field: string
  oldValue: string
  newValue: string
  createdAt: string
}

export interface TaskDetail extends Task {
  subtasks: Subtask[]
  comments: Comment[]
  attachments: Attachment[]
  activities: Activity[]
  watchers: UserRef[]
  isWatching: boolean
}

export interface Board {
  project: Project
  statuses: Status[]
  tasks: Task[]
  /**
   * True matches per column, which can exceed the tasks returned when a done
   * column is capped. Drives the header count and the "show older" affordance.
   *
   * Keyed the same way tasks are grouped — see `columnKey`.
   */
  counts: Record<string, number>
  /** The board spans this project and its sub-projects. */
  aggregated: boolean
  children: Project[]
}

/**
 * Which column a task or status belongs to.
 *
 * On an aggregated board each sub-project owns its own status rows, so two
 * projects' "In Review" are different ids and columns have to merge by name.
 * The server applies the identical rule when it counts, which is what keeps a
 * column header agreeing with the cards under it.
 */
export function columnKey(aggregated: boolean, statusId: string, statusName: string): string {
  return aggregated ? statusName.toLowerCase() : statusId
}

export type EventKind =
  | 'assigned'
  | 'mentioned'
  | 'commented'
  | 'status_changed'
  | 'due_soon'
  | 'overdue'

export interface NotificationPref {
  event: EventKind
  email: boolean
  slack: boolean
}

export interface ApiToken {
  id: string
  userId: string
  name: string
  prefix: string
  lastUsedAt: string | null
  createdAt: string
  /** What this token may do — a ceiling on the owner's own access. */
  scopes: Scope[]
  /** Empty means every project the owner can see. */
  projectIds: string[]
  projects?: ApiTokenProject[]
}

export interface Invitation {
  id: string
  email: string
  name: string
  role: Role
  invitedBy: string | null
  expiresAt: string
  acceptedAt: string | null
  createdAt: string
}

export interface SlackIdentity {
  userId: string
  slackUserId: string
  slackTeamId: string
  dmChannel: string
  linkedAt: string
}

export interface ExportResult {
  markdown: string
  tokens: number
  tasks: number
}

/** What an API token — and therefore an AI agent using it — may do. */
export type Scope = 'read' | 'write' | 'subtasks' | 'comments' | 'archive' | 'delete'

export const SCOPES: Array<{ id: Scope; label: string; danger?: boolean }> = [
  { id: 'read', label: 'Read projects, tasks and comments' },
  { id: 'write', label: 'Create and update tasks, and move them between columns' },
  { id: 'subtasks', label: 'Add subtasks and tick them off' },
  { id: 'comments', label: 'Post comments' },
  { id: 'archive', label: 'Archive tasks (hides them; nothing is lost)' },
  { id: 'delete', label: 'Delete tasks, projects and columns — permanent', danger: true },
]

/** Everything an agent needs, and nothing that cannot be undone. */
export const DEFAULT_SCOPES: Scope[] = ['read', 'write', 'subtasks', 'comments', 'archive']

export interface ApiTokenProject {
  id: string
  key: string
  name: string
}

export interface AdminStatus {
  outbox: { pending: number; sent24h: number; failed: number }
  liveConnections: number
  email: { configured: boolean; host: string; from: string; security: string }
  slack: { botConfigured: boolean; linkingConfigured: boolean }
  baseUrl: string
}

/** Where a delivery setting's value came from. */
export type SettingsSource = 'unset' | 'environment' | 'database'

/**
 * Delivery credentials as the admin screen sees them: what is in effect, where
 * it came from, and for secrets only whether one is set. Secrets are never
 * sent back, so a saved password cannot be read out of a form.
 */
export interface DeliverySettings {
  email: {
    host: string
    port: number
    username: string
    from: string
    fromName: string
    security: string
    hasPassword: boolean
    configured: boolean
    source: Record<string, SettingsSource>
  }
  slack: {
    hasBotToken: boolean
    hasClientId: boolean
    hasClientSecret: boolean
    hasSigningSecret: boolean
    clientId: string
    botConfigured: boolean
    canLink: boolean
    source: Record<string, SettingsSource>
  }
  updatedAt: string | null
  updatedBy: UserRef | null
}

/**
 * One save. Omit a field to leave it alone — which is how the password box can
 * stay empty without wiping what is stored. Send an empty string to clear.
 */
export interface EmailSettingsInput {
  host?: string
  port?: number
  username?: string
  password?: string
  from?: string
  fromName?: string
  security?: string
}

export interface SlackSettingsInput {
  botToken?: string
  clientId?: string
  clientSecret?: string
  signingSecret?: string
}

/** The live-sync payload. It names what changed, never the content itself. */
export interface BoardEvent {
  type:
    | 'task.created'
    | 'task.updated'
    | 'task.moved'
    | 'task.deleted'
    | 'comment.created'
    | 'statuses.changed'
    | 'project.updated'
  projectId: string
  taskId?: string
  actorId?: string
  at: string
}
